// Color math for the Brand Identity page: the dark palette a brand gets when it has not set one,
// and the WCAG contrast checks behind its warnings. deriveDarkColors should match the design
// studio's own derivation, which lives outside this repo; keep the two in step.

const HEX = /^#?([0-9a-f]{6})$/i;

export const isHexColor = (value) => typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value.trim());

const toRgb = (hex) => {
  const match = HEX.exec((hex ?? '').trim());
  if (!match) {
    return null;
  }
  const n = parseInt(match[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// `weight` of `a` plus the rest of `b`, as #RRGGBB. Returns `a` untouched when either is not a hex.
export const mixHex = (a, b, weight) => {
  const ra = toRgb(a);
  const rb = toRgb(b);
  if (!ra || !rb) {
    return a;
  }
  return `#${ra
    .map((channel, i) =>
      Math.round(channel * weight + rb[i] * (1 - weight))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`.toUpperCase();
};

// Perceived brightness 0..1 (the studio's yardstick for "is this a dark color"). Not a hex = light.
export const brightness = (hex) => {
  const rgb = toRgb(hex);
  if (!rgb) {
    return 1;
  }
  return (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
};

const isDark = (hex) => brightness(hex) < 0.5;

// Near-black text darkens to the same page and content color, so it is not used as the base.
const MIN_DARK_BASE = 0.1;

// Page and content from the brand's text color darkened, text from its page color, links from
// the primary color (lifted when it is too dark to read on a dark page), soft panels a touch of
// primary over content, dark panels a little lighter than content.
export const deriveDarkColors = (colors = {}) => {
  const base = isDark(colors.text) && brightness(colors.text) >= MIN_DARK_BASE ? colors.text : '#222222';
  const page = mixHex(base, '#000000', 0.35);
  const content = mixHex(base, '#000000', 0.6);
  const text = isDark(colors.page) || !isHexColor(colors.page) ? '#F4F4F4' : colors.page;
  const link = brightness(colors.primary) < 0.3 ? mixHex(colors.primary, '#FFFFFF', 0.55) : colors.primary;

  return {
    page,
    content,
    text,
    textMuted: mixHex(text, content, 0.35),
    link,
    soft: mixHex(link, content, 0.15),
    dark: mixHex('#FFFFFF', content, 0.08),
  };
};

// WCAG 2.x relative luminance and contrast ratio.
const relativeLuminance = (hex) => {
  const rgb = toRgb(hex);
  if (!rgb) {
    return null;
  }
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (a, b) => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) {
    return null;
  }
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

// AA for body text. An unreadable pair is only flagged when both colors are known.
export const AA_RATIO = 4.5;
export const failsAA = (a, b) => {
  const ratio = contrastRatio(a, b);
  return ratio !== null && ratio < AA_RATIO;
};

// RGBA pixels of a logo -> whether it would vanish on a dark background: mostly see-through, and
// what is drawn is dark. A logo on a solid (non-transparent) plate carries its own background.
export const logoVanishesOnDark = (pixels) => {
  let opaque = 0;
  let total = 0;
  let lightSum = 0;
  for (let i = 0; i < pixels.length; i += 4) {
    total += 1;
    if (pixels[i + 3] < 128) {
      continue;
    }
    opaque += 1;
    lightSum += (0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2]) / 255;
  }
  if (!total || !opaque || opaque / total > 0.9) {
    return false;
  }
  return lightSum / opaque < 0.35;
};
